import type { ReactElement, Ref } from 'react';
import { forwardRef, useEffect, useRef, useState } from 'react';

import { Loading } from '../loading';

import type { IImageProps } from './image.interface';
// import { useIntersection } from '../../../ui-hooks';
import { ImageFallback } from './image-fallback.component';

import './image.scss';

export const Image = forwardRef(
  (
    {
      title,
      src,
      rounded = 'none',
      srcset = [],
      disabled = false,
      fallback,
      onError,
      objectFit = 'cover',
      showLoader = false,
      imageHeight,
      imageWidth,
      className,
      fetchPriority = 'auto',
      loading = 'eager'
    }: IImageProps,
    ref: Ref<HTMLImageElement>
  ): ReactElement => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<boolean>(false);
    const targetRef = useRef<HTMLImageElement | null>(null);

    // useIntersection(targetRef, () => {
    //     setIsVisible(true);
    // });

    const handleError = (): void => {
      setError(true);
    };

    const handleImageLoad = () => {
      setIsLoading(false);
    };

    useEffect(() => {
      if (onError) {
        error && onError();
      }
    }, [error, onError]);

    const renderImage = () => {
      if (!src) {
        return <ImageFallback />;
      }

      if (error) {
        return null;
      }

      return (
        <img
          ref={ref}
          onError={handleError}
          onLoad={handleImageLoad}
          loading={loading}
          className={`
            ${objectFit === 'contain' ? 'co-object-contain' : 'co-object-cover'}
            co-w-full
            ${imageHeight ? imageHeight : ''}
            ${imageWidth ? imageWidth : ''}
            ${disabled ? 'co-grayscale co-opacity-40' : ''}
          `}
          src={src}
          alt={title}
          style={{
            display: showLoader && isLoading ? 'none' : 'block'
          }}
          {...{ fetchpriority: fetchPriority }}
        />
      );
    };

    return (
      <picture
        ref={targetRef}
        className={`
                co-flex
                co-w-full
                co-h-full
                co-overflow-hidden
                co-rounded-${rounded} ${className ? className : ''}`}
      >
        <>
          {srcset.length > 0 &&
            srcset.map((image, i) => (
              <source
                key={i}
                media={`(max-width: ${image.size}px)`}
                srcSet={image.url}
              />
            ))}
          {error && (fallback ? fallback : <ImageFallback />)}
          {showLoader && isLoading ? (
            <div className="co-relative co-h-full">
              <Loading />
            </div>
          ) : (
            renderImage()
          )}
        </>
      </picture>
    );
  }
);
